import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useAccountDuplicates, { useAccountMergePreview } from "../../data/useAccountDuplicates";
import { AccountType, IAccount } from "../../models/account/IAccount";
import { IAccountMergePreviewAccount } from "../../models/account/IDuplicates";
import { bankChoices } from "../../utils/contactDuplicates";
import { addToMerge, keepInstead } from "../../utils/contactMerges";
import useNotification from "../notifications/useNotifications";
import BankDetailsChoice from "../shared/Merge/BankDetailsChoice";
import MergeColumn, { IMergeField, IMergeStat } from "../shared/Merge/MergeColumn";
import MergeLayout from "../shared/Merge/MergeLayout";
import { SearchAccount } from "./SearchAccount";

interface AccountMergeModalProps {
  account: IAccount;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Folds duplicate accounts into this one by hand - for the duplicates the Duplicates page cannot spot
// on its own, such as the same company set up twice under slightly different names. The same dialog
// as the contact merge: every account side by side, kept first, before anything moves.
export function AccountMergeModal({ account, open, setOpen }: AccountMergeModalProps) {
  const { t } = useTranslation("accounts");
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { mergeAccounts } = useAccountDuplicates(false, "AccountMergeModal");
  const [keptId, setKeptId] = useState(account.id);
  const [otherIds, setOtherIds] = useState<string[]>([]);
  const [bankFromId, setBankFromId] = useState<string | null>(null);

  const preview = useAccountMergePreview(keptId, otherIds, "AccountMergeModal");
  const data = preview.data?.data;

  useEffect(() => {
    if (!open) {
      setKeptId(account.id);
      setOtherIds([]);
      setBankFromId(null);
    }
  }, [open, account.id]);

  const kept = data?.accounts.find((a) => a.isKept);
  const choices = data ? bankChoices(data, keptId, []) : [];
  const bankFrom = choices.find((c) => c.accountId === bankFromId) ?? choices[0] ?? null;

  const keep = (id: string) => {
    const next = keepInstead(keptId, otherIds, id);
    setKeptId(next.keptId);
    setOtherIds(next.otherIds);
  };

  const typeLabel = (type: AccountType) => (type === AccountType.Manual ? t("Regulars") : t(AccountType[type]));

  const fields = (row: IAccountMergePreviewAccount): IMergeField[] => [
    { label: t("Name"), value: row.name },
    { label: t("Type"), value: `${typeLabel(row.accountType)}${row.isHaulier ? ` · ${t("Haulier")}` : ""}` },
    { label: t("Bank details"), value: row.bankDetails },
    { label: t("VAT number"), value: row.vatNumber },
  ];

  const stats = (row: IAccountMergePreviewAccount): IMergeStat[] => [
    { label: t("Contacts"), count: row.contacts.length },
    { label: t("Invoices"), count: row.invoices },
    { label: t("Till sales"), count: row.tillSales },
    { label: t("Quotes"), count: row.salesQuotes },
    { label: t("Parts"), count: row.parts },
    { label: t("Addresses"), count: row.addresses },
  ];

  const moving: IMergeStat[] = data
    ? stats(data.accounts[0]).map((s, i) => ({
        label: s.label,
        count: data.accounts.filter((a) => !a.isKept).reduce((total, a) => total + stats(a)[i].count, 0),
      }))
    : [];

  const merge = () => {
    if (!data || data.refusal) return;

    mergeAccounts.mutate(
      {
        targetId: keptId,
        sourceIds: otherIds,
        bankDetailsFromAccountId: choices.length > 1 ? bankFrom?.accountId : undefined,
      },
      {
        onSuccess: (res) => {
          addNotification({ variant: "success", primaryText: res.message ?? t("Accounts merged") });
          setOpen(false);

          // The account this page was showing is now merged away: go to the one that was kept.
          if (keptId !== account.id) navigate(`/accounts/${keptId}`);
        },
        onError: (error: any) =>
          addNotification({ variant: "error", primaryText: t("The merge did not finish"), secondaryText: error?.message }),
      }
    );
  };

  const columns = (data?.accounts ?? []).map((row) => (
    <MergeColumn
      key={row.id}
      title={`#${row.accountNumber} ${row.name ?? ""}`.trim()}
      isKept={row.isKept}
      fields={fields(row)}
      keptFields={kept ? fields(kept) : []}
      stats={stats(row)}
      refusal={row.refusal}
      onRemove={row.isKept ? undefined : () => setOtherIds((current) => current.filter((x) => x !== row.id))}
      onKeep={row.isKept ? undefined : () => keep(row.id)}
    >
      {row.contacts.length > 0 && (
        <div className="text-sm">
          <p className="font-medium text-gray-700">{t("People on this account")}</p>
          <p className="text-gray-600">{row.contacts.map((c) => c.fullname || t("(no name)")).join(", ")}</p>
        </div>
      )}
    </MergeColumn>
  ));

  return (
    <MergeLayout
      open={open}
      setOpen={setOpen}
      title={t("Merge accounts")}
      intro={t("Pick the accounts that are the same customer. Their contacts, invoices, till sales, quotes, parts, addresses and notes move onto the one kept, and the others are removed. The kept account's own details win; its blanks are filled from the others.")}
      search={
        <SearchAccount
          label={t("Add an account to the merge")}
          selectedAccount={null}
          onAccountSelect={(picked: IAccount | null) => picked && setOtherIds((current) => addToMerge(keptId, current, picked.id))}
        />
      }
      columns={columns}
      hasSelection={otherIds.length > 0}
      emptyText={t("Search for the account to merge with this one. Both will appear side by side with what they hold.")}
      isLoading={preview.isLoading}
      isError={preview.isError}
      notices={
        choices.length > 1 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <BankDetailsChoice choices={choices} selectedId={bankFrom?.accountId ?? null} onSelect={setBankFromId} />
          </div>
        ) : null
      }
      moving={moving}
      keptName={kept ? `#${kept.accountNumber}` : undefined}
      fieldsFilled={data?.fieldsFilled}
      refusal={data?.refusal}
      confirmLabel={otherIds.length === 1 ? t("Merge 1 account") : t("Merge {{count}} accounts", { count: otherIds.length })}
      confirmDisabled={!data || !!data.refusal || otherIds.length === 0 || preview.isFetching}
      isConfirming={mergeAccounts.isLoading}
      onConfirm={merge}
    />
  );
}
